import type { CodeSessionRewindAction } from '@/data/code-sessions/mutations';
import type { CodeSessionRow, CodeSessionTurn } from '@/data/code-sessions/types';
import type { ForkPolicySession } from '@/lib/code-sessions/fork-policy';
import type { CodeSessionEvent } from '@/lib/code-sessions/types';
import type { SessionMessageInfo } from '@opencode/client/promise';

import type { ComposerDraft } from './code-composer-draft';

import { hiddenTurnIds, unsentTurnIds } from './transcript-rewind';

import { turnPromptAttachments } from '@/data/code-sessions/attachments';
import { applyRewindAction } from '@/data/code-sessions/rewind-cache';
import { turnPromptText } from '@/data/code-sessions/turn-prompt';
import { isLiveTurnStatus, turnPromptId } from '@/lib/code-sessions/turn-utils';

/** T3's send label while a rewind request is in flight. */
export const REWINDING_LABEL = 'Rewinding conversation';

/**
 * A session's saved turns as the transcript, Edit from here, Undo, and every
 * fork action see them. Computed once per session view, so they all agree.
 */
export type SessionTurnState = {
  /** Saved turns in index order. */
  turns: readonly CodeSessionTurn[];
  /** Discarded turns, plus a waiting rewind's boundary turn and every later one. */
  hidden: ReadonlySet<string>;
  /** Turns OpenCode holds no message for (`unsentTurnIds`). */
  unsent: ReadonlySet<string>;
  /**
   * What the shared rewind and fork policies decide on. An unsent turn is not
   * delivered, so the policies refuse it in their own order.
   */
  policy: ForkPolicySession;
  /** Each turn's prompt as its author wrote it, with the skills bound to its text and its files. */
  drafts: ReadonlyMap<string, ComposerDraft>;
  /** Turns whose Edit from here has no files to revert, so it need not ask about them. */
  noFilesToRevert: ReadonlySet<string>;
  /** A rewind request is in flight: its outcome already shows, and rewind and fork actions wait. */
  rewinding: boolean;
};

/** What only an open session knows beyond its saved row. */
export type LiveSessionEvidence = {
  /** The rewind request in flight; its outcome shows before the saved row has it. */
  pendingRewind: CodeSessionRewindAction | undefined;
  /** OpenCode's saved messages; null until the transcript loads. */
  messages: readonly SessionMessageInfo[] | null;
  events: readonly CodeSessionEvent[];
  isRunning: boolean;
  hasPendingForm: boolean;
  /** A Git commit or push in flight. */
  gitActionPending: boolean;
};

/** Without live evidence the saved row speaks for itself, as for a session that is not open. */
const SAVED_ONLY: LiveSessionEvidence = {
  pendingRewind: undefined,
  // OpenCode's messages live in the sandbox. Without them a prompt stopped
  // before delivery can still be listed; the fork then refuses it.
  messages: null,
  events: [],
  isRunning: false,
  hasPendingForm: false,
  gitActionPending: false,
};

/** Prompt IDs OpenCode holds as user messages: saved ones, and ones delivered since. */
function deliveredPromptIds(
  messages: readonly SessionMessageInfo[] | null,
  events: readonly CodeSessionEvent[],
): Set<string> {
  const delivered = new Set(
    (messages ?? []).filter((message) => message.type === 'user').map((message) => message.id),
  );
  for (const event of events) {
    if (event.kind === 'agent' && event.type === 'session.inbox.delivered') {
      const inboxId = (event.data as { inboxID?: unknown } | null)?.inboxID;
      if (typeof inboxId === 'string') {
        delivered.add(inboxId);
      }
    }
  }
  return delivered;
}

/**
 * Turns a rewind would restore no files for. OpenCode reverts files from each
 * assistant message's snapshot, so a turn qualifies only when every message
 * after its prompt finished a snapshot that changed nothing, and no turn from
 * it on is still live or already hidden by a rewind. Any doubt leaves it out.
 */
function turnsWithNoFilesToRevert(
  turns: readonly CodeSessionTurn[],
  messages: readonly SessionMessageInfo[] | null,
  hidden: ReadonlySet<string>,
): Set<string> {
  const clean = new Set<string>();
  if (messages === null) {
    return clean;
  }
  // Messages from this index on changed no files, as far as OpenCode recorded.
  let cleanFrom = messages.length;
  while (cleanFrom > 0) {
    const message = messages[cleanFrom - 1];
    if (
      message?.type === 'assistant' &&
      !(message.snapshot?.end !== undefined && message.snapshot.files?.length === 0)
    ) {
      break;
    }
    cleanFrom -= 1;
  }
  const messageIndex = new Map(messages.map((message, index) => [message.id, index]));
  let laterTurnsSettled = true;
  for (const turn of [...turns].reverse()) {
    laterTurnsSettled &&= !isLiveTurnStatus(turn.status) && !hidden.has(turn.id);
    const prompt = messageIndex.get(turnPromptId(turn));
    if (laterTurnsSettled && prompt !== undefined && prompt + 1 >= cleanFrom) {
      clean.add(turn.id);
    }
  }
  return clean;
}

/**
 * The prompt as its author wrote it, with the skills bound to its text and the
 * files it was sent with, which load from the session the turn belongs to.
 */
function draftOf(sessionId: string, turn: CodeSessionTurn): ComposerDraft {
  return {
    text: turnPromptText(turn),
    skills: turn.skills,
    attachments: turnPromptAttachments(sessionId, turn),
  };
}

export function sessionTurnState(
  saved: Pick<
    CodeSessionRow,
    | 'id'
    | 'status'
    | 'turns'
    | 'rewindTurnId'
    | 'rewindRestoreFiles'
    | 'sideAgentParentId'
    | 'purpose'
  > & {
    /** A setup-only session (`CodeSessionRow.setupOnly`), which neither rewinds nor forks. */
    setupOnly?: boolean;
  },
  live: LiveSessionEvidence = SAVED_ONLY,
): SessionTurnState {
  const row = applyRewindAction(saved, live.pendingRewind);
  const turns = [...row.turns].sort((a, b) => a.index - b.index);
  const unsent = unsentTurnIds(
    turns,
    deliveredPromptIds(live.messages, live.events),
    live.messages !== null,
  );
  const hidden = hiddenTurnIds(turns, row.rewindTurnId);
  return {
    turns,
    hidden,
    unsent,
    policy: {
      status: live.isRunning ? 'running' : row.status,
      // Delivery comes from OpenCode's messages: an unsent turn has none to rewind or fork before.
      turns: turns.map((turn) => ({ ...turn, delivered: !unsent.has(turn.id) })),
      sideAgentParentId: saved.sideAgentParentId,
      ...(saved.setupOnly ? { setupOnly: true } : {}),
      ...(saved.purpose === 'finding_fix' ? { findingConversation: true } : {}),
      hasPendingForm: live.hasPendingForm,
      gitActionPending: live.gitActionPending,
      rewindTurnId: row.rewindTurnId,
    },
    drafts: new Map(turns.map((turn) => [turn.id, draftOf(saved.id, turn)])),
    noFilesToRevert: turnsWithNoFilesToRevert(turns, live.messages, hidden),
    rewinding: live.pendingRewind !== undefined,
  };
}
